import { useState, useEffect, useRef } from 'react'
import {
  useClaimWorkflows, useClaimWorkflow,
  useCreateClaimWorkflow, useUpdateClaimWorkflow,
  useCreateWorkflowState, useUpdateWorkflowState, useDeleteWorkflowState,
  useCreateWorkflowAction, useUpdateWorkflowAction, useDeleteWorkflowAction,
  useFaultDefinitions, useCreateFaultDefinition, useUpdateFaultDefinition, useDeleteFaultDefinition,
  type WorkflowState, type WorkflowActionDef, type FaultDefinitionRow,
} from '@/admin/hooks/use-claim-workflows'
import { useFeatureFlag, FLAGS } from '@/admin/hooks/use-feature-flag'
import { useClaimSchemas } from '@/admin/hooks/use-claim-schemas'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/shared/components/ui/dialog'
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/shared/components/ui/select'
import { MasterDetail } from '@/admin/components/shared/master-detail'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import { Plus, Pencil, Trash2, ArrowRight, FileText, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/shared/lib/utils'

// ── Local Components ──────────────────────────────────────

import { toastApiError } from '@/shared/lib/api-error'
function GreenButton({ children, onClick, disabled, 'data-testid': dataTestId }: { children: React.ReactNode; onClick: () => void; disabled?: boolean; 'data-testid'?: string }) {
  return (
    <Button onClick={onClick} disabled={disabled} data-testid={dataTestId}
      variant="default"
      className="px-4 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer disabled:opacity-40 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
    >{children}</Button>
  )
}

function IconButton({ children, onClick, title, danger, 'data-testid': dataTestId }: { children: React.ReactNode; onClick: () => void; title: string; danger?: boolean; 'data-testid'?: string }) {
  return (
    <Button onClick={onClick} title={title} data-testid={dataTestId}
      variant="ghost"
      size="icon-xs"
      className={cn(
        'h-6 w-6 flex items-center justify-center rounded-[4px] transition-all duration-150 cursor-pointer hover:bg-[var(--bg-subtle)]',
        danger ? 'text-[var(--red)]' : 'text-[var(--ink-3)]',
      )}
    >{children}</Button>
  )
}

function TypeBadge({ children }: { children: React.ReactNode }) {
  return (<span className="inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">{children}</span>);
}

// ── Helper ────────────────────────────────────────────────

function toKey(label: string) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

// ── Applicability badge colors ────────────────────────────

const APPLICABILITY_COLORS: Record<string, string> = {
  CREATION: 'bg-[var(--green-bg-muted)] text-[var(--green)]',
  STATE: 'bg-[var(--scope-blue-bg)] text-[var(--status-info)]',
  GLOBAL: 'bg-[var(--scope-purple-bg)] text-[var(--ink-2)]',
}

// ── Main Page Component ─────────────────────────────────

export function ClaimWorkflowsPage() {
  const { data: workflows, isLoading } = useClaimWorkflows()
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  // Create workflow form state
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newSchemaUuid, setNewSchemaUuid] = useState<string>('')
  const createWorkflow = useCreateClaimWorkflow()
  const { data: schemas } = useClaimSchemas()

  const selected = (workflows ?? []).find((w) => w.uuid === selectedUuid) ?? null

  async function handleCreate() {
    if (!newName.trim()) return
    try {
      const created = await createWorkflow.mutateAsync({
        name: newName.trim(),
        description: newDescription.trim() || undefined,
        claim_schema_uuid: newSchemaUuid || null,
      })
      setSelectedUuid(created.uuid)
      setNewName('')
      setNewDescription('')
      setNewSchemaUuid('')
      setShowCreateDialog(false)
      toast.success('Workflow created')
    } catch (err) {
      toastApiError(err, 'Failed to create workflow');
    }
  }

  return (
    <>
      <MasterDetail
        testId="claim-workflows"
        title="Workflows"
        onAdd={() => setShowCreateDialog(true)}
        hasSelection={!!selectedUuid}
        selectedId={selectedUuid}
        visibleItemIds={(workflows ?? []).map((workflow) => workflow.uuid)}
        onSelectionChange={setSelectedUuid}
        onBack={() => setSelectedUuid(null)}
        list={
          <>
            {isLoading ? (
              <p className="text-[12px]" style={{ color: 'var(--ink-3)' }}>Loading...</p>
            ) : !(workflows ?? []).length ? (
              <p className="text-[12px]" style={{ color: 'var(--ink-3)' }}>No workflows yet</p>
            ) : (
              <div className="flex flex-col gap-0.5">
                {(workflows ?? []).map((wf) => {
                  const isActive = wf.uuid === selectedUuid
                  return (
                    <button
                      key={wf.uuid}
                      onClick={() => setSelectedUuid(wf.uuid)}
                      data-testid={`claim-workflows-item-${wf.uuid}`}
                      className="text-left px-3 py-2 rounded-[4px] text-[12px] transition-all duration-150 cursor-pointer"
                      style={{
                        color: isActive ? 'var(--green)' : 'var(--ink-2)',
                        background: isActive ? 'var(--green-bg-subtle)' : 'transparent',
                      }}
                      onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'var(--bg-subtle)' }}
                      onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent' }}
                    >
                      <span className="truncate block">{wf.name}</span>
                      {wf.claim_schema_name && (
                        <span className="text-[10px] block mt-0.5" style={{ color: 'var(--ink-3)' }}>
                          {wf.claim_schema_name}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        }
        detail={
          selected ? (
            <WorkflowDetail workflowUuid={selected.uuid} />
          ) : (
            <div className="flex justify-center pt-24">
              <p className="text-[13px] text-[var(--ink-3)]">
                Select a workflow to view details
              </p>
            </div>
          )
        }
      />
      {/* Create workflow dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Workflow</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div>
              <Label className="clara-section-label mb-1.5 block">Name</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Damage Claim Workflow"
                className="text-[13px]"
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate() }}
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Description</Label>
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Optional description"
                rows={3}
                className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Claim Schema</Label>
              <Select value={newSchemaUuid} onValueChange={setNewSchemaUuid}>
                <SelectTrigger size="sm" className="text-[11px] w-full">
                  <SelectValue placeholder="Select a schema (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {(schemas ?? []).map((s) => (
                    <SelectItem key={s.uuid} value={s.uuid}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <GreenButton onClick={handleCreate} disabled={!newName.trim() || createWorkflow.isPending} data-testid="claim-workflows-dialog-submit">
              Create
            </GreenButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Workflow Detail ─────────────────────────────────────

function WorkflowDetail({ workflowUuid }: { workflowUuid: string }) {
  const { data: workflow } = useClaimWorkflow(workflowUuid)
  const updateWorkflow = useUpdateClaimWorkflow()
  const createState = useCreateWorkflowState()
  const updateState = useUpdateWorkflowState()
  const deleteState = useDeleteWorkflowState()
  const createAction = useCreateWorkflowAction()
  const updateAction = useUpdateWorkflowAction()
  const deleteAction = useDeleteWorkflowAction()

  // ── Local form state ────────────────────────────────
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  // State dialog
  const [showStateDialog, setShowStateDialog] = useState(false)
  const [editingState, setEditingState] = useState<WorkflowState | null>(null)
  const [stKey, setStKey] = useState('')
  const [stLabel, setStLabel] = useState('')
  const [stColor, setStColor] = useState('#6b7280')
  const [stIsInitial, setStIsInitial] = useState(false)
  const [stIsTerminal, setStIsTerminal] = useState(false)
  const [stEditableFields, setStEditableFields] = useState('')
  const [stOrder, setStOrder] = useState(0)
  const [deletingState, setDeletingState] = useState<WorkflowState | null>(null)

  // Action dialog
  const [showActionDialog, setShowActionDialog] = useState(false)
  const [editingAction, setEditingAction] = useState<WorkflowActionDef | null>(null)
  const [actKey, setActKey] = useState('')
  const [actLabel, setActLabel] = useState('')
  const [actApplicability, setActApplicability] = useState<'CREATION' | 'STATE' | 'GLOBAL'>('STATE')
  const [actSourceStateUuid, setActSourceStateUuid] = useState('')
  const [actTargetStateUuid, setActTargetStateUuid] = useState('')
  const [actIcon, setActIcon] = useState('')
  const [actOrder, setActOrder] = useState(0)
  const [actAutoProceed, setActAutoProceed] = useState(false)
  const [actCaptureFields, setActCaptureFields] = useState('')
  const [actPrerequisites, setActPrerequisites] = useState('')
  const [actSideEffects, setActSideEffects] = useState('')
  const [actActor, setActActor] = useState<'ADMIN' | 'CONSUMER'>('ADMIN')
  const [deletingAction, setDeletingAction] = useState<WorkflowActionDef | null>(null)

  // Sync local state when workflow changes
  const prevUuidRef = useRef<string | null>(null)
  useEffect(() => {
    if (workflow && prevUuidRef.current !== workflow.uuid) {
      prevUuidRef.current = workflow.uuid
       
      setEditName(workflow.name)
      setEditDescription(workflow.description || '')
      setIsDirty(false)
    }
  }, [workflow])

  if (!workflow) {
    return <p className="text-[12px] text-[var(--ink-3)]">Loading...</p>;
  }

  const states = [...(workflow.states ?? [])].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
  const actions = [...(workflow.actions ?? [])].sort((a, b) => a.order - b.order)

  // ── Workflow save ───────────────────────────────────

  async function handleSaveWorkflow() {
    try {
      await updateWorkflow.mutateAsync({
        uuid: workflow!.uuid,
        data: { name: editName.trim(), description: editDescription.trim() },
      })
      setIsDirty(false)
      toast.success('Workflow updated')
    } catch (err) {
      toastApiError(err, 'Failed to update workflow');
    }
  }

  // ── State dialogs ──────────────────────────────────

  function openCreateStateDialog() {
    setEditingState(null)
    setStKey('')
    setStLabel('')
    setStColor('#6b7280')
    setStIsInitial(false)
    setStIsTerminal(false)
    setStEditableFields('')
    setStOrder(states.length)
    setShowStateDialog(true)
  }

  function openEditStateDialog(st: WorkflowState) {
    setEditingState(st)
    setStKey(st.key)
    setStLabel(st.label)
    setStColor(st.color || '#6b7280')
    setStIsInitial(st.is_initial)
    setStIsTerminal(st.is_terminal)
    setStEditableFields((st.editable_fields ?? []).join('\n'))
    setStOrder(st.order ?? 0)
    setShowStateDialog(true)
  }

  async function handleSaveState() {
    if (!stLabel.trim()) return
    const payload: Record<string, unknown> = {
      key: stKey.trim() || toKey(stLabel),
      label: stLabel.trim(),
      color: stColor.trim(),
      is_initial: stIsInitial,
      is_terminal: stIsTerminal,
      editable_fields: stEditableFields.trim() ? stEditableFields.trim().split('\n').map((f) => f.trim()).filter(Boolean) : [],
      order: stOrder,
    }
    try {
      if (editingState) {
        await updateState.mutateAsync({ workflowUuid: workflow!.uuid, stateUuid: editingState.uuid, data: payload })
        toast.success('State updated')
      } else {
        await createState.mutateAsync({ workflowUuid: workflow!.uuid, data: payload })
        toast.success('State created')
      }
      setShowStateDialog(false)
    } catch (err) {
      toastApiError(err, editingState ? 'Failed to update state' : 'Failed to create state');
    }
  }

  async function handleDeleteState() {
    if (!deletingState) return
    try {
      await deleteState.mutateAsync({ workflowUuid: workflow!.uuid, stateUuid: deletingState.uuid })
      toast.success('State deleted')
      setDeletingState(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete state');
    }
  }

  // ── Action dialogs ─────────────────────────────────

  function openCreateActionDialog() {
    setEditingAction(null)
    setActKey('')
    setActLabel('')
    setActApplicability('STATE')
    setActSourceStateUuid('')
    setActTargetStateUuid('')
    setActIcon('')
    setActOrder(actions.length)
    setActAutoProceed(false)
    setActCaptureFields('')
    setActPrerequisites('')
    setActSideEffects('')
    setActActor('ADMIN')
    setShowActionDialog(true)
  }

  function openEditActionDialog(act: WorkflowActionDef) {
    setEditingAction(act)
    setActKey(act.key)
    setActLabel(act.label)
    setActApplicability(act.applicability)
    setActSourceStateUuid(act.source_state_uuid ?? '')
    setActTargetStateUuid(act.target_state_uuid ?? '')
    setActIcon(act.icon ?? '')
    setActOrder(act.order)
    setActAutoProceed(act.auto_proceed)
    setActCaptureFields(act.capture_fields ? JSON.stringify(act.capture_fields, null, 2) : '')
    setActPrerequisites(act.prerequisites ? JSON.stringify(act.prerequisites, null, 2) : '')
    setActSideEffects(act.side_effects ? JSON.stringify(act.side_effects, null, 2) : '')
    setActActor(act.actor ?? 'ADMIN')
    setShowActionDialog(true)
  }

  async function handleSaveAction() {
    if (!actLabel.trim()) return

    let captureFieldsParsed: unknown[] = []
    if (actCaptureFields.trim()) {
      try { captureFieldsParsed = JSON.parse(actCaptureFields) } catch {
        toast.error('Invalid JSON in capture_fields'); return
      }
    }
    let prereqParsed: Record<string, unknown> = {}
    if (actPrerequisites.trim()) {
      try { prereqParsed = JSON.parse(actPrerequisites) } catch {
        toast.error('Invalid JSON in prerequisites'); return
      }
    }
    let sideEffectsParsed: Record<string, unknown> = {}
    if (actSideEffects.trim()) {
      try { sideEffectsParsed = JSON.parse(actSideEffects) } catch {
        toast.error('Invalid JSON in side_effects'); return
      }
    }

    const payload: Record<string, unknown> = {
      key: actKey.trim() || toKey(actLabel),
      label: actLabel.trim(),
      applicability: actApplicability,
      source_state_uuid: actApplicability === 'STATE' && actSourceStateUuid ? actSourceStateUuid : null,
      target_state_uuid: actTargetStateUuid || null,
      icon: actIcon.trim() || '',
      order: actOrder,
      auto_proceed: actAutoProceed,
      capture_fields: captureFieldsParsed,
      prerequisites: prereqParsed,
      side_effects: sideEffectsParsed,
      actor: actActor,
    }

    try {
      if (editingAction) {
        await updateAction.mutateAsync({ workflowUuid: workflow!.uuid, actionUuid: editingAction.uuid, data: payload })
        toast.success('Action updated')
      } else {
        await createAction.mutateAsync({ workflowUuid: workflow!.uuid, data: payload })
        toast.success('Action created')
      }
      setShowActionDialog(false)
    } catch (err) {
      toastApiError(err, editingAction ? 'Failed to update action' : 'Failed to create action');
    }
  }

  async function handleDeleteAction() {
    if (!deletingAction) return
    try {
      await deleteAction.mutateAsync({ workflowUuid: workflow!.uuid, actionUuid: deletingAction.uuid })
      toast.success('Action deleted')
      setDeletingAction(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete action');
    }
  }

  // ── Helpers for display ─────────────────────────────

  function stateLabel(uuid: string | null) {
    if (!uuid) return '---'
    const st = states.find((s) => s.uuid === uuid)
    return st ? st.label : '???'
  }

  return (
    <div>
      {/* Workflow name + description */}
      <div className="flex flex-col gap-3 mb-6">
        <Input
          value={editName}
          onChange={(e) => { setEditName(e.target.value); setIsDirty(true) }}
          className="border-none bg-transparent p-0 text-[16px]"
          placeholder="Workflow name"
        />
        <textarea
          value={editDescription}
          onChange={(e) => { setEditDescription(e.target.value); setIsDirty(true) }}
          rows={2}
          placeholder="Description (optional)"
          className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
        />
        <p className="text-[11px] text-[var(--ink-3)]">
          Schema: {workflow.claim_schema_name || '(none)'}
        </p>
        {isDirty && (
          <div>
            <GreenButton onClick={handleSaveWorkflow} disabled={updateWorkflow.isPending}>Save</GreenButton>
          </div>
        )}
      </div>
      {/* ── States Section ─────────────────────────────── */}
      <div
        className="mb-6 overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <span className="clara-section-label">States</span>
          <GreenButton onClick={openCreateStateDialog} data-testid="claim-workflows-add-state-btn">
            <span className="flex items-center gap-1"><Plus className="h-3 w-3" />Add State</span>
          </GreenButton>
        </div>

        {states.length === 0 ? (
          <p className="px-4 pb-4 text-[12px] text-[var(--ink-3)]">
            No states yet. Add one to define the workflow.
          </p>
        ) : (
          <div>
            {states.map((st, idx) => (
              <div
                key={st.uuid}
                className={cn(
                  'flex flex-wrap items-center gap-2 sm:gap-3 px-4 py-2',
                  idx < states.length - 1 && 'border-b-[0.5px] border-b-[var(--rule)]',
                )}
              >
                {/* Color dot */}
                <div className="shrink-0 rounded-full">
                  <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                    <circle cx="6" cy="6" r="6" fill={st.color || '#6b7280'} />
                  </svg>
                </div>
                {/* Label */}
                <span className="flex-1 truncate text-[13px] text-[var(--ink)]">
                  {st.label}
                </span>
                {/* Key badge */}
                <TypeBadge>{st.key}</TypeBadge>
                {/* is_initial badge */}
                {st.is_initial && (
                  <span className="inline-flex items-center rounded-full bg-[var(--green-bg-muted)] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.06em] text-[var(--green)]">Initial</span>
                )}
                {/* is_terminal badge */}
                {st.is_terminal && (
                  <span className="inline-flex items-center rounded-full bg-[var(--status-error-bg-subtle)] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.06em] text-[var(--red)]">Terminal</span>
                )}
                {/* Editable fields count */}
                {(st.editable_fields ?? []).length > 0 && (
                  <span className="text-[10px] text-[var(--ink-3)]">
                    {st.editable_fields.length} field{st.editable_fields.length !== 1 ? 's' : ''}
                  </span>
                )}
                {/* Edit / Delete */}
                <div className="flex items-center gap-0.5 shrink-0">
                  <IconButton onClick={() => openEditStateDialog(st)} title="Edit state" data-testid={`claim-workflows-edit-${st.uuid}`}>
                    <Pencil className="h-3 w-3" />
                  </IconButton>
                  <IconButton onClick={() => setDeletingState(st)} title="Delete state" danger data-testid={`claim-workflows-delete-${st.uuid}`}>
                    <Trash2 className="h-3 w-3" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* ── Actions Section ────────────────────────────── */}
      <div
        className="mb-6 overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]"
      >
        <div className="flex items-center justify-between px-4 py-3">
          <span className="clara-section-label">Actions</span>
          <GreenButton onClick={openCreateActionDialog} data-testid="claim-workflows-add-action-btn">
            <span className="flex items-center gap-1"><Plus className="h-3 w-3" />Add Action</span>
          </GreenButton>
        </div>

        {actions.length === 0 ? (
          <p className="px-4 pb-4 text-[12px] text-[var(--ink-3)]">
            No actions yet. Add one to define workflow transitions.
          </p>
        ) : (
          <div>
            {actions.map((act, idx) => {
              const applicabilityClass =
                APPLICABILITY_COLORS[act.applicability] ?? 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
              return (
                <div
                  key={act.uuid}
                  className={cn(
                    'flex flex-wrap items-center gap-2 sm:gap-3 px-4 py-2',
                    idx < actions.length - 1 && 'border-b-[0.5px] border-b-[var(--rule)]',
                  )}
                >
                  {/* Label */}
                  <span className="truncate text-[13px] text-[var(--ink)]">
                    {act.label}
                  </span>
                  {/* Key badge */}
                  <TypeBadge>{act.key}</TypeBadge>
                  {/* Applicability badge */}
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.06em]",
                      applicabilityClass,
                    )}
                  >
                    {act.applicability}
                  </span>
                  {/* Actor badge */}
                  {act.actor === 'CONSUMER' && (
                    <span className="inline-flex items-center rounded-full bg-[var(--blue-bg,#eff6ff)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--blue,#3b82f6)]">
                      CONSUMER
                    </span>
                  )}
                  {/* Source -> Target */}
                  {(act.source_state_uuid || act.target_state_uuid) && (
                    <span className="flex items-center gap-1 text-[11px] text-[var(--ink-3)]">
                      {stateLabel(act.source_state_uuid)}
                      <ArrowRight className="h-3 w-3" />
                      {stateLabel(act.target_state_uuid)}
                    </span>
                  )}
                  {/* Capture fields count */}
                  <span className="flex-1" />
                  {(act.capture_fields ?? []).length > 0 && (
                    <span className="text-[10px] text-[var(--ink-3)]">
                      {act.capture_fields!.length} capture field{act.capture_fields!.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {/* Edit / Delete */}
                  <div className="flex items-center gap-0.5 shrink-0">
                    <IconButton onClick={() => openEditActionDialog(act)} title="Edit action" data-testid={`claim-workflows-edit-${act.uuid}`}>
                      <Pencil className="h-3 w-3" />
                    </IconButton>
                    <IconButton onClick={() => setDeletingAction(act)} title="Delete action" danger data-testid={`claim-workflows-delete-${act.uuid}`}>
                      <Trash2 className="h-3 w-3" />
                    </IconButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* ── Faults Section ─────────────────────────────── */}
      <FaultsSection workflowUuid={workflow.uuid} />
      {/* ── State Dialog ──────────────────────────────── */}
      <Dialog open={showStateDialog} onOpenChange={setShowStateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingState ? 'Edit State' : 'Add State'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div>
              <Label className="clara-section-label mb-1.5 block">Label</Label>
              <Input
                value={stLabel}
                onChange={(e) => {
                  setStLabel(e.target.value)
                  if (!editingState) setStKey(toKey(e.target.value))
                }}
                placeholder="e.g. Under Review"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Key</Label>
              <Input
                value={stKey}
                onChange={(e) => setStKey(e.target.value)}
                placeholder="e.g. under_review"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={stColor}
                  onChange={(e) => setStColor(e.target.value)}
                  placeholder="#6b7280"
                  className="text-[13px] flex-1"
                />
                <div className="shrink-0 rounded-[4px]">
                  <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
                    <rect x="0.5" y="0.5" width="27" height="27" rx="4" fill={stColor || '#6b7280'} stroke="var(--rule)" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox id="st-initial" checked={stIsInitial} onCheckedChange={(checked) => setStIsInitial(checked === true)} />
                <Label htmlFor="st-initial" className="text-[12px] text-[var(--ink-2)]">Initial</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="st-terminal" checked={stIsTerminal} onCheckedChange={(checked) => setStIsTerminal(checked === true)} />
                <Label htmlFor="st-terminal" className="text-[12px] text-[var(--ink-2)]">Terminal</Label>
              </div>
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Editable Fields (one per line)</Label>
              <textarea
                value={stEditableFields}
                onChange={(e) => setStEditableFields(e.target.value)}
                rows={3}
                placeholder="field_one&#10;field_two"
                className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 font-mono text-[12px] text-[var(--ink)]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Order</Label>
              <Input
                type="number"
                value={stOrder}
                onChange={(e) => setStOrder(parseInt(e.target.value) || 0)}
                className="text-[13px] w-24"
              />
            </div>
          </div>
          <DialogFooter>
            <GreenButton
              onClick={handleSaveState}
              disabled={!stLabel.trim() || createState.isPending || updateState.isPending}
              data-testid="claim-workflows-state-dialog-submit"
            >
              {editingState ? 'Save' : 'Create'}
            </GreenButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ── Action Dialog ─────────────────────────────── */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAction ? 'Edit Action' : 'Add Action'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2 max-h-[60vh] overflow-y-auto">
            <div>
              <Label className="clara-section-label mb-1.5 block">Label</Label>
              <Input
                value={actLabel}
                onChange={(e) => {
                  setActLabel(e.target.value)
                  if (!editingAction) setActKey(toKey(e.target.value))
                }}
                placeholder="e.g. Approve Claim"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Key</Label>
              <Input
                value={actKey}
                onChange={(e) => setActKey(e.target.value)}
                placeholder="e.g. approve_claim"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Applicability</Label>
              <Select value={actApplicability} onValueChange={(v) => setActApplicability(v as 'CREATION' | 'STATE' | 'GLOBAL')}>
                <SelectTrigger size="sm" className="text-[11px] w-full">
                  <SelectValue>{actApplicability}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CREATION">CREATION</SelectItem>
                  <SelectItem value="STATE">STATE</SelectItem>
                  <SelectItem value="GLOBAL">GLOBAL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Actor</Label>
              <Select value={actActor} onValueChange={(v) => setActActor(v as 'ADMIN' | 'CONSUMER')}>
                <SelectTrigger size="sm" className="text-[11px] w-full">
                  <SelectValue>{actActor}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">ADMIN</SelectItem>
                  <SelectItem value="CONSUMER">CONSUMER</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {actApplicability === 'STATE' && (
              <div>
                <Label className="clara-section-label mb-1.5 block">Source State</Label>
                <Select value={actSourceStateUuid} onValueChange={setActSourceStateUuid}>
                  <SelectTrigger size="sm" className="text-[11px] w-full">
                    <SelectValue placeholder="Select source state" />
                  </SelectTrigger>
                  <SelectContent>
                    {states.map((s) => (
                      <SelectItem key={s.uuid} value={s.uuid}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="clara-section-label mb-1.5 block">Target State (optional)</Label>
              <Select value={actTargetStateUuid} onValueChange={setActTargetStateUuid}>
                <SelectTrigger size="sm" className="text-[11px] w-full">
                  <SelectValue placeholder="Select target state" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((s) => (
                    <SelectItem key={s.uuid} value={s.uuid}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Icon (optional)</Label>
              <Input
                value={actIcon}
                onChange={(e) => setActIcon(e.target.value)}
                placeholder="e.g. check-circle"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Order</Label>
              <Input
                type="number"
                value={actOrder}
                onChange={(e) => setActOrder(parseInt(e.target.value) || 0)}
                className="text-[13px] w-24"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="act-auto-proceed" checked={actAutoProceed} onCheckedChange={(checked) => setActAutoProceed(checked === true)} />
              <Label htmlFor="act-auto-proceed" className="text-[12px] text-[var(--ink-2)]">Auto Proceed</Label>
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Capture Fields (JSON array)</Label>
              <textarea
                value={actCaptureFields}
                onChange={(e) => setActCaptureFields(e.target.value)}
                rows={3}
                placeholder='[{"key": "reason", "label": "Reason"}]'
                className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 font-mono text-[12px] text-[var(--ink)]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Prerequisites (JSON object)</Label>
              <textarea
                value={actPrerequisites}
                onChange={(e) => setActPrerequisites(e.target.value)}
                rows={3}
                placeholder='{}'
                className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 font-mono text-[12px] text-[var(--ink)]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Side Effects (JSON object)</Label>
              <textarea
                value={actSideEffects}
                onChange={(e) => setActSideEffects(e.target.value)}
                rows={3}
                placeholder='{}'
                className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 font-mono text-[12px] text-[var(--ink)]"
              />
            </div>
          </div>
          <DialogFooter>
            <GreenButton
              onClick={handleSaveAction}
              disabled={!actLabel.trim() || createAction.isPending || updateAction.isPending}
              data-testid="claim-workflows-action-dialog-submit"
            >
              {editingAction ? 'Save' : 'Create'}
            </GreenButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={!!deletingState}
        onOpenChange={(next) => {
          if (!next) setDeletingState(null)
        }}
        title={`Delete ${deletingState?.label ?? 'state'}?`}
        description="This removes the state from the claim workflow. Actions that reference it may need to be reviewed."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={deleteState.isPending}
        onConfirm={handleDeleteState}
      />
      <ConfirmationDialog
        open={!!deletingAction}
        onOpenChange={(next) => {
          if (!next) setDeletingAction(null)
        }}
        title={`Delete ${deletingAction?.label ?? 'action'}?`}
        description="This removes the action from the claim workflow. Users will no longer be able to trigger it."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={deleteAction.isPending}
        onConfirm={handleDeleteAction}
      />
    </div>
  );
}

// ── Faults Section ─────────────────────────────────────

function FaultsSection({ workflowUuid }: { workflowUuid: string }) {
  const hasFaultDefinitions = useFeatureFlag(FLAGS.FAULT_DEFINITIONS)
  const { data: faults } = useFaultDefinitions(hasFaultDefinitions ? workflowUuid : null)
  const createFault = useCreateFaultDefinition()
  const updateFault = useUpdateFaultDefinition()
  const deleteFault = useDeleteFaultDefinition()

  const [showDialog, setShowDialog] = useState(false)
  const [editing, setEditing] = useState<FaultDefinitionRow | null>(null)
  const [fKey, setFKey] = useState('')
  const [fLabel, setFLabel] = useState('')
  const [fDescription, setFDescription] = useState('')
  const [fCategory, setFCategory] = useState('')
  const [fOrder, setFOrder] = useState(0)
  const [fIsActive, setFIsActive] = useState(true)
  const [deleting, setDeleting] = useState<FaultDefinitionRow | null>(null)

  if (!hasFaultDefinitions) return null

  function openCreateDialog() {
    setEditing(null)
    setFKey('')
    setFLabel('')
    setFDescription('')
    setFCategory('')
    setFOrder((faults ?? []).length)
    setFIsActive(true)
    setShowDialog(true)
  }

  function openEditDialog(f: FaultDefinitionRow) {
    setEditing(f)
    setFKey(f.key)
    setFLabel(f.label)
    setFDescription(f.description)
    setFCategory(f.category)
    setFOrder(f.order)
    setFIsActive(f.is_active)
    setShowDialog(true)
  }

  async function handleSave() {
    if (!fLabel.trim()) return
    const payload: Record<string, unknown> = {
      key: fKey.trim() || toKey(fLabel),
      label: fLabel.trim(),
      description: fDescription.trim(),
      category: fCategory.trim(),
      order: fOrder,
      is_active: fIsActive,
    }
    try {
      if (editing) {
        await updateFault.mutateAsync({ workflowUuid, faultUuid: editing.uuid, data: payload })
        toast.success('Fault updated')
      } else {
        await createFault.mutateAsync({ workflowUuid, data: payload })
        toast.success('Fault created')
      }
      setShowDialog(false)
    } catch (err) {
      toastApiError(err, editing ? 'Failed to update fault' : 'Failed to create fault')
    }
  }

  async function handleDelete() {
    if (!deleting) return
    try {
      await deleteFault.mutateAsync({ workflowUuid, faultUuid: deleting.uuid })
      toast.success('Fault deleted')
      setDeleting(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete fault')
    }
  }

  const sorted = [...(faults ?? [])].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))

  return (
    <>
      <div className="mb-6 overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]">
        <div className="flex items-center justify-between px-4 py-3">
          <span className="clara-section-label">Faults</span>
          <GreenButton onClick={openCreateDialog} data-testid="claim-workflows-add-fault-btn">
            <span className="flex items-center gap-1"><Plus className="h-3 w-3" />Add Fault</span>
          </GreenButton>
        </div>

        {sorted.length === 0 ? (
          <p className="px-4 pb-4 text-[12px] text-[var(--ink-3)]">
            No faults defined. Add faults to attach packet and checklist documents per defect type.
          </p>
        ) : (
          <div>
            {sorted.map((f, idx) => (
              <div
                key={f.uuid}
                className={cn(
                  'flex flex-wrap items-center gap-2 sm:gap-3 px-4 py-2',
                  idx < sorted.length - 1 && 'border-b-[0.5px] border-b-[var(--rule)]',
                )}
              >
                <span className="truncate text-[13px] text-[var(--ink)]">{f.label}</span>
                <TypeBadge>{f.key}</TypeBadge>
                {f.category && (
                  <span className="text-[10px] text-[var(--ink-3)]">{f.category}</span>
                )}
                {!f.is_active && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-[var(--status-error-bg-subtle)] px-1.5 py-0.5 text-[9px] uppercase tracking-[0.06em] text-[var(--red)]">
                    <AlertCircle className="h-2.5 w-2.5" />Inactive
                  </span>
                )}
                <span className="flex-1" />
                {f.packet_document_filename && (
                  <span className="flex items-center gap-1 text-[10px] text-[var(--ink-3)]" title={`Packet: ${f.packet_document_filename}`}>
                    <FileText className="h-3 w-3" />Packet
                  </span>
                )}
                {f.checklist_document_filename && (
                  <span className="flex items-center gap-1 text-[10px] text-[var(--ink-3)]" title={`Checklist: ${f.checklist_document_filename}`}>
                    <FileText className="h-3 w-3" />Checklist
                  </span>
                )}
                <div className="flex items-center gap-0.5 shrink-0">
                  <IconButton onClick={() => openEditDialog(f)} title="Edit fault">
                    <Pencil className="h-3 w-3" />
                  </IconButton>
                  <IconButton onClick={() => setDeleting(f)} title="Delete fault" danger>
                    <Trash2 className="h-3 w-3" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Fault' : 'Add Fault'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div>
              <Label className="clara-section-label mb-1.5 block">Label</Label>
              <Input
                value={fLabel}
                onChange={(e) => {
                  setFLabel(e.target.value)
                  if (!editing) setFKey(toKey(e.target.value))
                }}
                placeholder="e.g. Blowoff"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Key</Label>
              <Input
                value={fKey}
                onChange={(e) => setFKey(e.target.value)}
                placeholder="e.g. blowoff"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Category</Label>
              <Input
                value={fCategory}
                onChange={(e) => setFCategory(e.target.value)}
                placeholder="Maps to major_category value"
                className="text-[13px]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Description</Label>
              <textarea
                value={fDescription}
                onChange={(e) => setFDescription(e.target.value)}
                rows={2}
                placeholder="Optional description"
                className="w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 text-[13px] text-[var(--ink)]"
              />
            </div>
            <div>
              <Label className="clara-section-label mb-1.5 block">Order</Label>
              <Input
                type="number"
                value={fOrder}
                onChange={(e) => setFOrder(parseInt(e.target.value) || 0)}
                className="text-[13px] w-24"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="fault-active" checked={fIsActive} onCheckedChange={(checked) => setFIsActive(checked === true)} />
              <Label htmlFor="fault-active" className="text-[12px] text-[var(--ink-2)]">Active</Label>
            </div>
          </div>
          <DialogFooter>
            <GreenButton
              onClick={handleSave}
              disabled={!fLabel.trim() || createFault.isPending || updateFault.isPending}
              data-testid="claim-workflows-fault-dialog-submit"
            >
              {editing ? 'Save' : 'Create'}
            </GreenButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete ${deleting?.label ?? 'fault'}?`}
        description="This removes the fault definition and its document associations from this workflow."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={deleteFault.isPending}
        onConfirm={handleDelete}
      />
    </>
  )
}
